/**
 * Interest Calculation Engine - Single source of truth for ALL interest calculations
 *
 * This module implements pure functions with no side effects:
 * - 30/360 day count convention calculations (used for ALL interest calculations)
 * - Period-based interest calculations with proper rounding
 * - Bond payment interest calculations with future obligations
 * - Interest rate retrieval from bond payment maps
 * - Interest start date determination logic
 * - Helper methods for rate normalization and validation
 *
 * IMPORTANT: All interest calculations in the system MUST use this engine.
 * Do not implement interest calculations elsewhere.
 */

import { TIME_PERIODS } from '~/config/financial-constants';
import type { BondSeries, CashFlow } from '~/types';

export class InterestCalculationEngine {
  /**
   * Calculates the number of days between two dates using the 30/360 day count convention
   * Pure function with no side effects - ported from prototype's date-utils.ts
   */
  static days360(startDate: string, endDate: string): number {
    const parseDate = (dateString: string) => {
      const [yearStr, monthStr, dayStr] = dateString.split('-');
      return {
        year: parseInt(yearStr, 10),
        month: parseInt(monthStr, 10),
        day: parseInt(dayStr, 10),
      };
    };

    const { year: y1, month: m1, day: day1 } = parseDate(startDate);
    const { year: y2, month: m2, day: day2 } = parseDate(endDate);

    // Per 30/360 US convention
    const d1 = day1 === 31 ? 30 : day1;
    const d2 = day2 === 31 && d1 === 30 ? 30 : day2;

    return (y2 - y1) * TIME_PERIODS.DAYS_PER_YEAR + (m2 - m1) * 30 + (d2 - d1);
  }

  /**
   * Calculates interest for a period using 30/360 day count convention
   * Pure function with no side effects - ported from prototype's date-utils.ts
   */
  static calculateInterestForPeriod(
    principal: number,
    annualRate: number,
    startDate: string,
    endDate: string
  ): number {
    const numberOfDays = InterestCalculationEngine.days360(startDate, endDate);
    const interest = principal * annualRate * (numberOfDays / TIME_PERIODS.DAYS_PER_YEAR);
    return interest;
  }

  /**
   * Enhanced bond payment interest calculation with future interest obligations
   * Based on engine-prototype's calculateBondPaymentInterest using 30/360 day count
   * Pure function that returns calculated values without modifying input parameters
   */
  static calculateBondPaymentInterest(
    principal: number,
    interestRate: number,
    startDate: string,
    endDate: string,
    existingFutureInterestObligation: number
  ): { newInterest: number; totalInterest: number; futureInterestObligation: number } {
    // Calculate new interest using proper 30/360 day count convention
    const newInterest = InterestCalculationEngine.calculateInterestForPeriod(
      principal,
      interestRate,
      startDate,
      endDate
    );

    // Trust the future interest obligation from cascading (now properly prorated)
    // No adjustment needed here - cascading engine handles first payment proration
    const adjustedFutureInterest = existingFutureInterestObligation;

    // Calculate totals with proper rounding
    const roundedNewInterest = Math.round(newInterest);
    const totalInterest = Math.round(newInterest + adjustedFutureInterest);

    return {
      newInterest: roundedNewInterest,
      totalInterest: totalInterest,
      futureInterestObligation: existingFutureInterestObligation, // Keep existing value - cascading will update this
    };
  }

  /**
   * Gets the starting rate from bond's interest rate escalation settings
   */
  private static getStartRate(bondSeries: BondSeries): number {
    const bondWithEscalation = bondSeries as BondSeries & {
      __interestRateEscalation?: { startRate: number };
    };
    return bondWithEscalation.__interestRateEscalation?.startRate || 0.05; // Default to 5%
  }

  /**
   * Gets the actual interest rate for a specific payment from the bond's payment map
   * Pure function that safely retrieves interest rates with fallback to starting rate
   */
  static getInterestRateForPayment(bondSeries: BondSeries, paymentDate: string): number {
    if (!bondSeries.bondPaymentMap || Object.keys(bondSeries.bondPaymentMap).length === 0) {
      const startRate = InterestCalculationEngine.getStartRate(bondSeries);
      console.warn(
        `No payment map found for bond ${bondSeries.name}, using starting rate ${startRate}`
      );
      return startRate;
    }

    // Find the payment date key in the bond payment map
    for (const [_, entry] of Object.entries(bondSeries.bondPaymentMap)) {
      if (entry.paymentDate === paymentDate) {
        return entry.interestRate;
      }
    }

    // If not found, use starting rate as fallback
    const startRate = InterestCalculationEngine.getStartRate(bondSeries);
    console.warn(
      `Payment date ${paymentDate} not found in bond ${bondSeries.name} payment map, using starting rate ${startRate}`
    );
    return startRate;
  }

  /**
   * Determines the correct start date for interest calculation
   * Based on the engine-prototype's determineInterestStartDate logic
   * Pure function that analyzes cash flow records to determine interest period
   */
  static determineInterestStartDate(
    bondSeriesId: string,
    _currentPaymentDate: string,
    nextRecord: CashFlow | null,
    bondSeries: BondSeries
  ): string {
    // Check if there's a next payment for this specific bond series
    const nextPaymentForThisBond = nextRecord?.bondPayments.find(
      (p) => p.bondSeriesId === bondSeriesId
    );

    if (nextPaymentForThisBond?.isActive) {
      // The bond series continues. The period is between this payment and the next.
      return nextRecord!.paymentDate;
    } else {
      // This is the first payment for this bond series. The period starts from its issue date.
      return bondSeries.issueDate;
    }
  }

  /**
   * Normalizes interest rate to decimal format
   * Handles both percentage (5.0) and decimal (0.05) inputs
   * @param rate - Interest rate as percentage or decimal
   * @returns Interest rate as decimal (e.g., 0.05 for 5%)
   */
  static normalizeInterestRate(rate: number): number {
    // If rate is greater than 1, assume it's a percentage
    if (rate > 1) {
      return rate / 100;
    }
    return rate;
  }

  /**
   * Calculates interest for ANY period using 30/360 day count
   * This is now the ONLY method for interest calculations in the system
   *
   * @param principal - Principal amount
   * @param annualRate - Annual interest rate (as decimal or percentage)
   * @param startDate - Start date of interest period (YYYY-MM-DD)
   * @param endDate - End date of interest period (YYYY-MM-DD)
   * @returns Interest amount for the period
   */
  static calculateInterest(
    principal: number,
    annualRate: number,
    startDate: string,
    endDate: string
  ): number {
    const normalizedRate = InterestCalculationEngine.normalizeInterestRate(annualRate);
    return InterestCalculationEngine.calculateInterestForPeriod(
      principal,
      normalizedRate,
      startDate,
      endDate
    );
  }

  /**
   * Calculates interest when only the number of days is known
   * Useful when days have been pre-calculated for efficiency
   *
   * @param principal - Principal amount
   * @param annualRate - Annual interest rate (as decimal or percentage)
   * @param days - Number of days (using 30/360 convention)
   * @returns Interest amount for the period
   */
  static calculateInterestFromDays(principal: number, annualRate: number, days: number): number {
    const normalizedRate = InterestCalculationEngine.normalizeInterestRate(annualRate);
    const interest = principal * normalizedRate * (days / TIME_PERIODS.DAYS_PER_YEAR);
    return interest;
  }

  /**
   * Helper method to calculate standard semi-annual interest
   * Replaces the old "divide by payments per year" approach
   *
   * @param principal - Principal amount
   * @param annualRate - Annual interest rate (as decimal or percentage)
   * @returns Interest amount for a standard 6-month period
   */
  static calculateSemiAnnualInterest(principal: number, annualRate: number): number {
    const normalizedRate = InterestCalculationEngine.normalizeInterestRate(annualRate);
    // Standard semi-annual payment is 180 days in 30/360 convention
    return InterestCalculationEngine.calculateInterestFromDays(
      principal,
      normalizedRate,
      TIME_PERIODS.STANDARD_PERIOD_DAYS
    );
  }

  /**
   * Calculates interest for UI display purposes
   * Always returns annual interest amount
   *
   * @param principal - Principal amount
   * @param annualRate - Annual interest rate (as decimal or percentage)
   * @returns Annual interest amount
   */
  static calculateAnnualInterest(principal: number, annualRate: number): number {
    const normalizedRate = InterestCalculationEngine.normalizeInterestRate(annualRate);
    return principal * normalizedRate;
  }

  /**
   * Enhanced bond payment interest calculation that uses pre-calculated days when available
   * This is the most efficient method when processing bond payments with payment maps
   *
   * @param principal - Principal amount
   * @param interestRate - Interest rate from payment map
   * @param startDate - Start date of interest period
   * @param endDate - End date of interest period
   * @param existingFutureInterestObligation - Future interest obligations
   * @param daysToPrior - Pre-calculated days from prior payment (optional)
   * @returns Interest calculation results
   */
  static calculateBondPaymentInterestEfficient(
    principal: number,
    interestRate: number,
    startDate: string,
    endDate: string,
    existingFutureInterestObligation: number,
    daysToPrior?: number
  ): { newInterest: number; totalInterest: number; futureInterestObligation: number } {
    // Use pre-calculated days if available, otherwise calculate
    const days = daysToPrior ?? InterestCalculationEngine.days360(startDate, endDate);

    // Calculate new interest using the day count
    const newInterest = InterestCalculationEngine.calculateInterestFromDays(
      principal,
      interestRate,
      days
    );

    // Trust the future interest obligation from cascading (no adjustment needed)
    // Cascading engine handles first payment proration
    const adjustedFutureInterest = existingFutureInterestObligation;

    // Calculate totals with proper rounding
    const roundedNewInterest = Math.round(newInterest);
    const totalInterest = Math.round(newInterest + adjustedFutureInterest);

    return {
      newInterest: roundedNewInterest,
      totalInterest: totalInterest,
      futureInterestObligation: existingFutureInterestObligation,
    };
  }

  /**
   * Helper to get interest rate and pre-calculated days from bond payment map
   * Returns all the data needed for efficient interest calculation
   *
   * @param bondSeries - Bond series with payment map
   * @param paymentDate - Payment date to look up
   * @returns Object with interest rate and day counts
   */
  static getPaymentMapData(
    bondSeries: BondSeries,
    paymentDate: string
  ): {
    interestRate: number;
    daysToPrior?: number;
    daysToNext?: number;
  } {
    if (!bondSeries.bondPaymentMap || Object.keys(bondSeries.bondPaymentMap).length === 0) {
      const startRate = InterestCalculationEngine.getStartRate(bondSeries);
      console.warn(
        `No payment map found for bond ${bondSeries.name}, using starting rate ${startRate}`
      );
      return { interestRate: startRate };
    }

    // Find the payment date entry in the bond payment map
    for (const [_, entry] of Object.entries(bondSeries.bondPaymentMap)) {
      if (entry.paymentDate === paymentDate) {
        return {
          interestRate: entry.interestRate,
          daysToPrior: entry.daysToPrior,
          daysToNext: entry.daysToNext,
        };
      }
    }

    // If not found, use starting rate as fallback
    const startRate = InterestCalculationEngine.getStartRate(bondSeries);
    console.warn(
      `Payment date ${paymentDate} not found in bond ${bondSeries.name} payment map, using starting rate ${startRate}`
    );
    return { interestRate: startRate };
  }
}
